
import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { deleteAndReinitializeDatabase, initDB } from '@/lib/db/indexedDB';

export const DatabaseInitializer: React.FC = () => {
  const { toast } = useToast();

  // Initialize database on component mount
  useEffect(() => {
    const initializeDatabase = async () => {
      try {
        await initDB();
        console.log("Database initialized successfully");
      } catch (error) {
        console.error("Error initializing database:", error);
        toast({
          variant: "destructive",
          title: "Database initialization failed",
          description: "There was a problem initializing the local database. Some features may not work properly."
        });
      }
    };

    initializeDatabase();
  }, [toast]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log("App is online");
      toast({
        title: "Back Online",
        description: "Connection restored. Syncing pending changes..."
      });
      
      // Request sync when back online
      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        navigator.serviceWorker.ready.then(registration => {
          // Check if sync is available before using it
          try {
            // Type checking for registration
            const syncRegistration = registration as any;
            if (syncRegistration.sync && typeof syncRegistration.sync.register === 'function') {
              syncRegistration.sync.register('sync-user-data').catch((err: Error) => {
                console.error('Error registering sync-user-data:', err);
              });
              
              syncRegistration.sync.register('sync-inventory').catch((err: Error) => {
                console.error('Error registering sync-inventory:', err);
              });
            } else {
              console.warn("Background sync is not supported by this browser");
            }
          } catch (err) {
            console.warn("Background sync failed:", err);
          }
        }).catch(err => {
          console.error('Error registering sync:', err);
        });
      }
    };

    const handleOffline = () => {
      console.log("App is offline");
      toast({
        variant: "destructive",
        title: "Offline Mode",
        description: "You're currently offline. Changes will be saved locally and synchronized when connectivity is restored."
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  const handleReset = async () => {
    try {
      await deleteAndReinitializeDatabase();
      toast({
        title: "Database reset successful",
        description: "The database has been reset. Please refresh the page to reinitialize with fresh data.",
      });
    } catch (error) {
      console.error('Error resetting database:', error);
      toast({
        variant: "destructive",
        title: "Database reset failed",
        description: "There was an error resetting the database. Please try again.",
      });
    }
  };

  return (
    <div className="fixed bottom-4 right-4">
      <Button 
        variant="outline" 
        size="sm"
        onClick={handleReset}
      >
        Reset Database
      </Button>
    </div>
  );
};

export default DatabaseInitializer;
